import { ChevronIcon } from './WalletShared';
import { TRADABLE_TOKENS } from '@uo/shared';
import { tokenDisplayLabel } from '@/utils/orb-display';

import { COLORS, EPSELL_COLORS, WALLET_COLORS as C } from '@/constants';
import { TransactionSecurity } from '../ui/TransactionSecurity';

type Translate = (key: string) => string;

export function TransferModal({
  t,
  open,
  toUsername,
  transferAsset,
  assetDropdownOpen,
  transferAmount,
  transferMemo,
  transferTxPwd,
  transferSecAnswer,
  showSecurity,
  error,
  busy,
  onClose,
  onUsernameChange,
  onAssetDropdownToggle,
  onAssetChange,
  onAmountChange,
  onMemoChange,
  onTxPwdChange,
  onSecurityAnswerChange,
  onSecurityToggle,
  onSubmit,
}: {
  t: Translate;
  open: boolean;
  toUsername: string;
  transferAsset: string;
  assetDropdownOpen: boolean;
  transferAmount: string;
  transferMemo: string;
  transferTxPwd: string;
  transferSecAnswer: string;
  showSecurity: boolean;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onUsernameChange: (value: string) => void;
  onAssetDropdownToggle: () => void;
  onAssetChange: (value: string) => void;
  onAmountChange: (value: string) => void;
  onMemoChange: (value: string) => void;
  onTxPwdChange: (value: string) => void;
  onSecurityAnswerChange: (value: string) => void;
  onSecurityToggle: () => void;
  onSubmit: () => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4 py-5 backdrop-blur-sm">
      <div className="w-full max-w-[382px] rounded-2xl border p-5 shadow-2xl" style={{ background: COLORS.bg, borderColor: C.border }} role="dialog" aria-modal="true" aria-labelledby="transfer-modal-title">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div id="transfer-modal-title" className="text-base font-semibold" style={{ color: COLORS.iconGlobal }}>{t('transfer.title')}</div>
          <button type="button" onClick={onClose} className="flex size-8 shrink-0 items-center justify-center rounded-full border text-lg leading-none text-white/80" style={{ background: COLORS.bgOther, borderColor: C.border}} aria-label={t('common.close')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M14.9073 2.84663C15.256 2.58792 15.7513 2.61637 16.0675 2.93256C16.4153 3.28037 16.4153 3.84452 16.0675 4.19233L10.7599 9.49995L16.0675 14.8076C16.4153 15.1554 16.4153 15.7195 16.0675 16.0673C15.7513 16.3835 15.256 16.412 14.9073 16.1533L14.8077 16.0673L9.50012 10.7597L4.1925 16.0673L4.09289 16.1533C3.74423 16.412 3.24892 16.3835 2.93273 16.0673C2.58492 15.7195 2.58492 15.1554 2.93273 14.8076L8.24035 9.49995L2.93273 4.19233C2.58492 3.84452 2.58492 3.28037 2.93273 2.93256C3.24892 2.61637 3.74423 2.58792 4.09289 2.84663L4.1925 2.93256L9.50012 8.24018L14.8077 2.93256L14.9073 2.84663Z" fill={COLORS.iconGlobal}/></svg>
          </button>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <span className="block text-xs font-medium" style={{ color: COLORS.iconColor }}>{t('transfer.toUser')}</span>
            <input className="h-10 w-full rounded-lg border border-[#F5EC63]/30 bg-black/35 px-3 text-sm font-semibold text-white outline-none placeholder:text-white/50 focus:border-[#ADD741]" value={toUsername} onChange={(e) => onUsernameChange(e.target.value)} autoCapitalize="none" />
          </div>
          <div className="flex flex-col gap-2 relative z-20">
            <span className="block text-xs font-medium" style={{ color: COLORS.iconColor }}>{t('transfer.asset')}</span>
            <button type="button" className="flex h-10 w-full items-center justify-between rounded-lg border border-[#F5EC63]/30 bg-black/35 px-3 text-left font-mono text-base font-semibold text-[#E5E7EB] outline-none transition-colors focus:border-[#ADD741]" onClick={onAssetDropdownToggle}>
              <span>{tokenDisplayLabel(transferAsset)}</span>
              <ChevronIcon up={assetDropdownOpen} />
            </button>
            {assetDropdownOpen && (
              <div className="absolute left-0 right-0 top-[68px] z-50 max-h-48 overflow-y-auto rounded-xl border border-[#F5EC63]/40 bg-[#07100A]/95 p-1 shadow-[0_10px_28px_rgba(0,0,0,0.55),0_0_18px_rgba(245,236,99,0.20)] backdrop-blur-md">
                {TRADABLE_TOKENS.map((tok: string) => (
                  <button key={tok} type="button" className={`flex h-10 w-full items-center rounded-lg px-3 text-left font-mono text-sm font-semibold transition-colors ${tok === transferAsset ? 'bg-[#ADD741]/20 text-[#F5EC63]' : 'text-[#E5E7EB] hover:bg-[#F5EC63]/10'}`} onClick={() => onAssetChange(tok)}>
                    {tokenDisplayLabel(tok)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative z-10 grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-2">
              <span className="block text-xs font-medium" style={{ color: COLORS.iconColor }}>{t('transfer.amount')}</span>
              <input className="h-10 w-full rounded-lg border border-[#F5EC63]/30 bg-black/35 px-3 font-mono text-base font-semibold text-white outline-none placeholder:text-white/50 focus:border-[#ADD741]" inputMode="decimal" value={transferAmount} onChange={(e) => onAmountChange(e.target.value)} />
            </label>
            <label className="flex flex-col gap-2">
              <span className="block text-xs font-medium" style={{ color: COLORS.iconColor }}>{t('transfer.memo')}</span>
              <input className="h-10 w-full rounded-lg border border-[#F5EC63]/30 bg-black/35 px-3 text-sm font-semibold text-white outline-none placeholder:text-white/50 focus:border-[#ADD741]" value={transferMemo} onChange={(e) => onMemoChange(e.target.value)} />
            </label>
          </div>
          <TransactionSecurity
            txPassword={transferTxPwd}
            onTxPasswordChange={onTxPwdChange}
            showSecurity={showSecurity}
            onShowSecurityChange={onSecurityToggle}
            securityAnswer={transferSecAnswer}
            onSecurityAnswerChange={onSecurityAnswerChange}
            colors={{
              muted: COLORS.iconColor,
              red: '#F74E5E',
              activeGreen: '#ADD741',
              gold: '#F5EC63',
              softBorder: 'rgba(245,236,99,0.3)',
              inputBg: 'rgba(0,0,0,0.35)',
            }}
          />
          {error && <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>}
          <button type="button" disabled={busy || !toUsername.trim() || !transferAmount.trim() || !transferTxPwd.trim()} onClick={onSubmit} className="mt-3 h-11 w-full rounded-xl border text-sm font-semibold transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:brightness-75 disabled:opacity-60" style={{ background: EPSELL_COLORS.buttonBg, borderColor: EPSELL_COLORS.buttonBorder, boxShadow: EPSELL_COLORS.buttonShadow, color: EPSELL_COLORS.text }}>
            {busy ? '…' : t('transfer.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

